import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { DonutInsightCard, type DonutOptionSelection } from './DonutInsightCard';
import { FrequencyInsightCard } from './FrequencyInsightCard';
import { hasInsightData } from './insightMath';
import type { Insight, InsightGroup, InsightGroupVariant } from './types';

export type InsightGroupSectionProps = {
  group: InsightGroup;
  /** Grid groups collapse to this many cards until "See more" is clicked. */
  collapseAfter?: number;
  /** Drill-down into the door-visit list. Frequency cards stay non-interactive regardless. */
  onOptionSelect?: (selection: DonutOptionSelection) => void;
};

// auto-fit/minmax instead of a fixed column count: a group with fewer cards
// than the old repeat(N, 1fr) grid gets fewer, wider columns rather than
// every card being squeezed into a 1/N-width slot regardless of its content
// (custom-slot titles/labels are broker-authored free text of any length).
// min(Npx, 100%) caps the floor at the viewport width so a phone-width screen
// still gets one full-width column instead of forcing a wider-than-viewport
// track (and the horizontal scroll that comes with it).
const columnsFor = (variant: InsightGroupVariant | undefined) =>
  variant === 'featured'
    ? 'repeat(auto-fit, minmax(min(420px, 100%), 1fr))'
    : 'repeat(auto-fit, minmax(min(360px, 100%), 1fr))';

// A frequency card always spans the full grid row (it's a wide bar list, not a
// grid tile); single-select donuts flow into the grid's normal columns.
const renderInsight = (
  insight: Insight,
  showCounts: boolean,
  onOptionSelect?: (selection: DonutOptionSelection) => void,
) => {
  switch (insight.kind) {
    case 'single_select':
      return <DonutInsightCard insight={insight} showCounts={showCounts} onOptionSelect={onOptionSelect} />;
    case 'frequency':
      return <FrequencyInsightCard insight={insight} />;
    default: {
      // Exhaustiveness guard: TypeScript rejects a new InsightKind here at
      // compile time until a branch is added. A live payload that doesn't match
      // the declared union (e.g. a Core schema change ahead of this frontend)
      // still reaches this at runtime — warn instead of silently dropping it.
      const unrecognized: never = insight;
      console.warn('InsightGroupSection: unrecognized insight kind, skipping card', unrecognized);
      return null;
    }
  }
};

export const InsightGroupSection = ({ group, collapseAfter = 8, onOptionSelect }: InsightGroupSectionProps) => {
  const [expanded, setExpanded] = useState(false);

  // Render in the exact order Core returned (types.ts's documented contract) —
  // no reordering by kind. A frequency card spans the full row via `gridColumn`
  // so it can sit inline with the donut grid at whatever position it occurs.
  const insights = group.insights.filter(hasInsightData);

  const isGrid = group.variant !== 'featured';
  const collapsible = isGrid && insights.length > collapseAfter;
  const visible = collapsible && !expanded ? insights.slice(0, collapseAfter) : insights;
  const hiddenCount = insights.length - collapseAfter;

  return (
    <Box component="section" aria-label={group.label}>
      <Stack sx={{ mb: 2, gap: 0.25 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 0.8 }}>
          {group.label}
        </Typography>
        {group.description && (
          <Typography variant="body2" color="text.secondary">
            {group.description}
          </Typography>
        )}
      </Stack>

      {visible.length > 0 && (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: columnsFor(group.variant) }}>
          {visible.map(insight => (
            <Box
              key={insight.id}
              sx={insight.kind === 'frequency' ? { gridColumn: '1 / -1', minWidth: 0 } : { minWidth: 0 }}
            >
              {renderInsight(insight, group.variant === 'featured', onOptionSelect)}
            </Box>
          ))}
        </Box>
      )}

      {collapsible && (
        <Stack direction="row" sx={{ justifyContent: 'flex-end', mt: 1.5 }}>
          <Button
            size="small"
            color="primary"
            endIcon={<ExpandMoreOutlinedIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none' }} />}
            onClick={() => setExpanded(current => !current)}
          >
            {expanded ? 'See less' : `See more (${hiddenCount}+)`}
          </Button>
        </Stack>
      )}
    </Box>
  );
};
